import { Buffer } from 'buffer'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'

import type {
  BankAccountInterface,
  InvoiceData,
  OrganisationInterface,
} from '@/types'
import { getVatAmount, withVat } from '@/utils/price/vat'

import {
  formatInvoiceDeliveryTime,
  formatInvoiceIssueDate,
  formatInvoiceProductName,
  getPrintLogo,
  getPrintStamp,
  INVOICE_VALIDITY_TEXT,
  type InvoiceType,
  isRrcInvoice,
  numberToWordsRu,
} from './invoiceUtils'

if (typeof window !== 'undefined') {
  window.Buffer = window.Buffer || Buffer
}

const processImageBackground = async (url: string): Promise<ArrayBuffer> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'Anonymous'
    img.src = url

    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext('2d')
      if (!ctx) return reject('Canvas context error')

      ctx.drawImage(img, 0, 0)
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      const data = imageData.data

      for (let i = 0; i < data.length; i += 4) {
        const r = data[i]
        const g = data[i + 1]
        const b = data[i + 2]
        if (r > 240 && g > 240 && b > 240) {
          data[i + 3] = 0
        }
      }

      ctx.putImageData(imageData, 0, 0)

      canvas.toBlob((blob) => {
        if (blob) {
          blob.arrayBuffer().then(resolve).catch(reject)
        } else {
          reject('Blob creation error')
        }
      }, 'image/png')
    }

    img.onerror = reject
  })
}

const formatAddressLine = (parts: (string | undefined | null | false)[]) =>
  parts.filter((part) => part && part !== '').join(', ')

export const exportInvoiceToExcel = async (
  invoiceData: InvoiceData,
  type: InvoiceType
) => {
  const { orderDetails, supplier, supplierBank, client, clientBank, payer } =
    invoiceData
  const isRRC = isRrcInvoice(type)
  const order = orderDetails.orderModel
  const issuedAt = new Date()

  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('Счет')

  const lastCol = isRRC ? 'E' : 'I'

  worksheet.columns = isRRC
    ? [
        { width: 18 }, // A - №
        { width: 61 }, // B - Товар
        { width: 16 }, // C - Кол-во
        { width: 16 }, // D - Цена
        { width: 20 }, // E - Всего
      ]
    : [
        { width: 13 }, // A - №
        { width: 40 }, // B - Товар
        { width: 12 }, // C - Кол-во
        { width: 16 }, // D - Срок доставки
        { width: 12 }, // E - Цена
        { width: 12 }, // F - Сумма
        { width: 10 }, // G - Ст. НДС
        { width: 12 }, // H - Сумма НДС
        { width: 15 }, // I - Всего
      ]

  try {
    const logoBuffer = await processImageBackground(getPrintLogo())
    const logoId = workbook.addImage({ buffer: logoBuffer, extension: 'png' })
    worksheet.addImage(logoId, {
      tl: { col: isRRC ? 3 : 6, row: 0 },
      ext: { width: 130, height: 45 },
    })
  } catch (e) {
    console.error('Logo error', e)
  }

  const titleRow = worksheet.getRow(1)
  titleRow.height = 30
  const titleCell = worksheet.getCell('A1')
  titleCell.value = `Счет на оплату № ${order.code} от ${formatInvoiceIssueDate(
    issuedAt
  )}`
  titleCell.font = { name: 'Arial', bold: true, size: 14 }
  worksheet.mergeCells(isRRC ? 'A1:C1' : 'A1:E1')

  const addInfo = (rowNum: number, label: string, details: string) => {
    const row = worksheet.getRow(rowNum)
    row.height = 50
    row.getCell(1).value = label
    row.getCell(1).font = { bold: true }

    row.getCell(2).value = details
    row.getCell(2).alignment = { wrapText: true, vertical: 'top' }
    worksheet.mergeCells(`B${rowNum}:${lastCol}${rowNum}`)
  }

  const orgDetails = (
    org: OrganisationInterface | undefined,
    bank: BankAccountInterface | null | undefined
  ) =>
    formatAddressLine([
      org?.vatNumber
        ? `${org.fullLegalName || org.description}, УНП ${org.vatNumber}`
        : org?.description || org?.shortLegalName,
      org?.legalAddress && `юр. адрес: ${org.legalAddress}`,
      bank?.iban && `р/с № ${bank.iban}`,
      bank?.bankDto?.description && `в ${bank.bankDto.description}`,
      bank?.bankDto?.code && `БИК ${bank.bankDto.code}`,
      org?.phoneNumber && `тел. ${org.phoneNumber}`,
      org?.email && `e-mail: ${org.email}`,
    ])

  const payerDetails = formatAddressLine([
    payer?.description,
    payer?.address && `адрес: ${payer.address}`,
    payer?.ownerName && `владелец: ${payer.ownerName}`,
    payer?.ownerPhoneNumber && `тел. ${payer.ownerPhoneNumber}`,
  ])

  addInfo(3, 'Поставщик:', orgDetails(supplier, supplierBank))
  addInfo(
    4,
    'Плательщик:',
    isRRC ? payerDetails : orgDetails(client, clientBank)
  )

  if (order?.contractShortModel) {
    const contractText = `Договор № ${order.contractShortModel.number} от ${
      order.contractShortModel.creationDate
        ? new Date(order.contractShortModel.creationDate).toLocaleDateString(
            'ru-RU'
          )
        : ''
    }`

    const contractRow = worksheet.addRow([contractText])
    contractRow.font = { italic: true }
    worksheet.mergeCells(
      `A${contractRow.number}:${lastCol}${contractRow.number}`
    )
  }

  const protocolRow = worksheet.addRow([
    'Счет на оплату является протоколом согласования цен',
  ])
  protocolRow.font = { italic: true }
  worksheet.mergeCells(`A${protocolRow.number}:${lastCol}${protocolRow.number}`)

  if (order.comment) {
    const commentRow = worksheet.addRow([`Комментарий: ${order.comment}`])
    commentRow.font = { bold: true }
    worksheet.mergeCells(`A${commentRow.number}:${lastCol}${commentRow.number}`)
  }

  worksheet.addRow([])

  const headerRow = worksheet.addRow(
    isRRC
      ? ['№', 'Товар', 'Кол-во', 'Цена', 'Всего']
      : [
          '№',
          'Товар',
          'Кол-во',
          'Срок доставки\n(банковских)',
          'Цена',
          'Сумма',
          'Ст. НДС',
          'Сумма НДС',
          'Всего',
        ]
  )
  headerRow.eachCell((cell) => {
    cell.font = { bold: true }
    cell.alignment = { horizontal: 'center' }
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF0F0F0' },
    }
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    }
  })
  if (!isRRC) {
    headerRow.getCell(4).alignment = {
      horizontal: 'center',
      vertical: 'middle',
      wrapText: true,
    }
  }

  let totalAmount = 0
  let totalVat = 0
  let totalFinal = 0
  let idx = 1

  orderDetails.products.forEach((product) => {
    if (product.status === 'REJECTED') return

    let price: number
    let amount: number
    let vat: number
    let total: number
    let displayPrice: number

    if (isRRC) {
      price = product.retailPrice || 0
      amount = price * product.quantity
      vat = getVatAmount(amount)
      total = amount + vat
      displayPrice = withVat(price)
    } else {
      price = product.price
      amount = product.amount
      vat = product.vatAmount
      total = product.total
      displayPrice = price
    }

    totalAmount += amount
    totalVat += vat
    totalFinal += total

    const productFullName = formatInvoiceProductName(
      product.nomenclatureDto?.article,
      product.nomenclatureDto?.description,
      type
    )
    const deliveryTime = formatInvoiceDeliveryTime(
      product.storeShortModel?.etaMaxDays
    )

    const row = worksheet.addRow(
      isRRC
        ? [idx++, productFullName, product.quantity, displayPrice, total]
        : [
            idx++,
            productFullName,
            product.quantity,
            deliveryTime,
            price,
            amount,
            '20%',
            vat,
            total,
          ]
    )

    row.eachCell((cell) => {
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      }
      cell.alignment = { vertical: 'middle' }
    })

    row.getCell(3).numFmt = '0.####'
    const moneyColumns = isRRC ? [4, 5] : [5, 6, 8, 9]
    moneyColumns.forEach((column) => {
      row.getCell(column).numFmt = '0.00'
    })
  })

  const sumRow = worksheet.addRow(
    isRRC
      ? ['', '', '', 'ИТОГО:', totalFinal]
      : ['', '', '', '', 'ИТОГО:', totalAmount, 'X', totalVat, totalFinal]
  )
  sumRow.font = { bold: true }
  sumRow.eachCell((cell, colNumber) => {
    if (colNumber >= (isRRC ? 4 : 6)) {
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      }
    }
  })
  const totalMoneyColumns = isRRC ? [5] : [6, 8, 9]
  totalMoneyColumns.forEach((column) => {
    sumRow.getCell(column).numFmt = '0.00'
  })

  worksheet.addRow([])

  if (!isRRC) {
    const vatWordsRow = worksheet.addRow([
      `Сумма НДС: ${numberToWordsRu(totalVat)}`,
    ])
    vatWordsRow.font = { italic: true }
    worksheet.mergeCells(
      `A${vatWordsRow.number}:${lastCol}${vatWordsRow.number}`
    )
  }

  const totalWordsRow = worksheet.addRow([
    `Всего к оплате: ${numberToWordsRu(totalFinal)}`,
  ])
  totalWordsRow.font = { bold: true }
  worksheet.mergeCells(
    `A${totalWordsRow.number}:${lastCol}${totalWordsRow.number}`
  )

  const validityRow = worksheet.addRow([INVOICE_VALIDITY_TEXT])
  validityRow.font = { italic: true }
  worksheet.mergeCells(`A${validityRow.number}:${lastCol}${validityRow.number}`)

  if (isRRC) {
    worksheet.addRow([])

    const signRow = worksheet.addRow([])
    signRow.getCell(1).value = 'Руководитель'
    signRow.getCell(1).font = { bold: true }
    signRow.getCell(2).border = { bottom: { style: 'thin' } }

    signRow.getCell(4).border = { bottom: { style: 'thin' } }
    signRow.getCell(5).border = { bottom: { style: 'thin' } }
    worksheet.mergeCells(`D${signRow.number}:E${signRow.number}`)

    const captionRow = worksheet.addRow([])
    captionRow.getCell(4).value = 'расшифровка подписи'
    captionRow.getCell(4).font = { size: 8, italic: true }
    captionRow.getCell(4).alignment = { horizontal: 'center' }
    worksheet.mergeCells(`D${captionRow.number}:E${captionRow.number}`)
  } else {
    try {
      const stampBuffer = await processImageBackground(getPrintStamp())
      const stampId = workbook.addImage({
        buffer: stampBuffer,
        extension: 'png',
      })
      worksheet.addImage(stampId, {
        tl: { col: 1, row: worksheet.lastRow!.number + 1 },
        ext: { width: 750, height: 230 },
      })
    } catch (e) {
      console.error('Stamp error', e)
    }
  }

  const buffer = await workbook.xlsx.writeBuffer()
  const fileName = isRRC
    ? `Счет на оплату РРЦ${type === 'RRC_HIDDEN' ? ' (Скрытый)' : ''} №${order.code}.xlsx`
    : `Счет на оплату клиенту №${order.code}.xlsx`

  saveAs(new Blob([buffer]), fileName)
}
